import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStores } from '../../../stores';
import partnerServiceService from '../../../services/partnerServiceService';
import partnerService from '../../../services/partnerService';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Switch,
  message,
  Spin,
  Divider,
  Space,
  Result
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined, ShopOutlined, EnvironmentOutlined } from '@ant-design/icons';
import './Services.scss';

const { TextArea } = Input;
const { Option } = Select;

const ServiceAddEdit = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { partnerServiceStore } = useStores();
  const { currentService, loading } = partnerServiceStore;

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [itemsInfo, setItemsInfo] = useState(null);
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(!isEdit);

  useEffect(() => {
    // Fetch service categories from API
    const fetchCategories = async () => {
      const result = await partnerServiceService.getServiceCategories();
      if (result.success) {
        setServiceCategories(result.data);
      } else {
        console.error('Failed to fetch service categories:', result.error);
        message.error(t('partner.services.messages.loadCategoriesFailed'));
      }
    };

    fetchCategories();

    if (isEdit) {
      partnerServiceStore.fetchServiceById(id);
    } else {
      // Check item limit and location only when adding new service
      checkRequirements();
    }
  }, [id, isEdit, partnerServiceStore]);

  const checkRequirements = async () => {
    setCheckingLimit(true);
    // Check both item limit and partner location
    const [itemsResult, profileResult] = await Promise.all([
      partnerService.fetchItemsInfo(),
      partnerService.fetchMyProfile()
    ]);
    if (itemsResult.success) {
      setItemsInfo(itemsResult.data);
    }
    if (profileResult.success) {
      setPartnerProfile(profileResult.data);
    }
    setCheckingLimit(false);
  };

  const isLimitReached = !isEdit && itemsInfo && itemsInfo.items_remaining <= 0;
  const isLocationMissing = !isEdit && partnerProfile && (!partnerProfile.city || !partnerProfile.address);

  useEffect(() => {
    // Only set form values when we have the service data AND categories are loaded
    if (isEdit && currentService && serviceCategories.length > 0) {
      form.setFieldsValue({
        service_category: currentService.service_category,
        name_en: currentService.name_en,
        name_el: currentService.name_el,
        description_en: currentService.description_en,
        description_el: currentService.description_el,
        requires_booking: currentService.requires_booking !== false,
        duration: currentService.duration,
        duration_unit: currentService.duration_unit || 'MINUTES',
        capacity: currentService.capacity,
        price: parseFloat(currentService.price),
        is_active: currentService.is_active
      });
    }
  }, [currentService, isEdit, form, serviceCategories]);

  const handleSubmit = async (values) => {
    setSubmitting(true);

    const result = isEdit
      ? await partnerServiceStore.updateService(id, values)
      : await partnerServiceStore.createService(values);

    setSubmitting(false);

    if (result.success) {
      message.success(isEdit ? t('partner.services.messages.updateSuccess') : t('partner.services.messages.createSuccess'));
      navigate('/partner/services');
    } else {
      message.error(result.error);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  // Show loading while checking limit
  if (checkingLimit) {
    return (
      <div className="loading-container">
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('partner.products.checkingAvailability')}</div>
      </div>
    );
  }

  // Show location required screen for add mode
  if (isLocationMissing) {
    return (
      <div className="service-add-edit">
        <div className="page-header">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/partner/services')}
          >
            {t('partner.services.backToServices')}
          </Button>
        </div>

        <Result
          status="warning"
          icon={<EnvironmentOutlined style={{ color: '#faad14' }} />}
          title={t('partner.services.locationRequired')}
          subTitle={t('partner.services.locationRequiredDesc')}
          extra={[
            <Button type="primary" key="profile" onClick={() => navigate('/partner/profile')}>
              {t('partner.services.goToProfile')}
            </Button>,
            <Button key="back" onClick={() => navigate('/partner/services')}>
              {t('partner.services.backToServices')}
            </Button>
          ]}
        />
      </div>
    );
  }

  // Show limit reached screen for add mode
  if (isLimitReached) {
    return (
      <div className="service-add-edit">
        <div className="page-header">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/partner/services')}
          >
            {t('partner.services.backToServices')}
          </Button>
        </div>

        <Result
          status="warning"
          icon={<ShopOutlined style={{ color: '#faad14' }} />}
          title={t('partner.products.limitReached')}
          subTitle={t('partner.products.limitReachedDescItems', { max: itemsInfo.max_items })}
          extra={[
            <Button type="primary" key="packages" onClick={() => navigate('/partner/packages')}>
              {t('partner.products.getMoreSlots')}
            </Button>,
            <Button key="back" onClick={() => navigate('/partner/services')}>
              {t('partner.services.backToServices')}
            </Button>
          ]}
        />
      </div>
    );
  }

  return (
    <div className="service-add-edit">
      <div className="page-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/partner/services')}
        >
          {t('partner.services.backToServices')}
        </Button>
        <h1>{isEdit ? t('partner.services.editService') : t('partner.services.addService')}</h1>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            requires_booking: true,
            duration: 30,
            duration_unit: 'MINUTES',
            capacity: 1
          }}
        >
          <Divider orientation="left">{t('partner.services.form.basicInfo')}</Divider>

          <Form.Item
            name="service_category"
            label={t('partner.services.form.category')}
            rules={[{ required: true, message: t('partner.services.form.categoryRequired') }]}
          >
            <Select placeholder={t('partner.services.form.selectCategory')} size="large">
              {serviceCategories.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.name_en}</Option>
              ))}
            </Select>
          </Form.Item>

          <Divider orientation="left">{t('partner.services.form.serviceName')}</Divider>

          <Form.Item
            name="name_en"
            label={t('partner.services.form.nameEn')}
            rules={[
              { required: true, message: t('partner.services.form.nameEnRequired') },
              { max: 255, message: t('partner.services.form.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('partner.services.form.nameEnPlaceholder')} size="large" />
          </Form.Item>

          <Form.Item
            name="name_el"
            label={t('partner.services.form.nameEl')}
            rules={[
              { required: true, message: t('partner.services.form.nameElRequired') },
              { max: 255, message: t('partner.services.form.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('partner.services.form.nameElPlaceholder')} size="large" />
          </Form.Item>

          <Divider orientation="left">{t('partner.services.form.description')}</Divider>

          <Form.Item
            name="description_en"
            label={t('partner.services.form.descriptionEn')}
            rules={[
              { required: true, message: t('partner.services.form.descriptionEnRequired') }
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t('partner.services.form.descriptionEnPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="description_el"
            label={t('partner.services.form.descriptionEl')}
            rules={[
              { required: true, message: t('partner.services.form.descriptionElRequired') }
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t('partner.services.form.descriptionElPlaceholder')}
            />
          </Form.Item>

          <Divider orientation="left">{t('partner.services.form.serviceDetails')}</Divider>

          <Form.Item
            name="requires_booking"
            label={t('partner.services.form.requiresBooking')}
            valuePropName="checked"
            tooltip={t('partner.services.form.requiresBookingTooltip')}
          >
            <Switch
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
            />
          </Form.Item>

          {/* Conditional booking fields - only shown when requires_booking is true */}
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.requires_booking !== currentValues.requires_booking}>
            {({ getFieldValue }) => {
              const requiresBooking = getFieldValue('requires_booking');
              if (!requiresBooking) return null;

              return (
                <>
                  <Space align="start" style={{ width: '100%' }}>
                    <Form.Item
                      name="duration"
                      label={t('partner.services.form.duration')}
                      rules={[
                        { required: true, message: t('partner.services.form.durationRequired') },
                        { type: 'number', min: 1, message: t('partner.services.form.durationMin') }
                      ]}
                      style={{ width: 200 }}
                    >
                      <InputNumber
                        min={1}
                        step={1}
                        style={{ width: '100%' }}
                        placeholder="30"
                      />
                    </Form.Item>

                    <Form.Item
                      name="duration_unit"
                      label={t('partner.services.form.unit')}
                      rules={[{ required: true, message: t('partner.services.form.unitRequired') }]}
                      style={{ width: 150 }}
                    >
                      <Select>
                        <Option value="MINUTES">{t('common.minutes')}</Option>
                        <Option value="HOURS">{t('common.hours')}</Option>
                        <Option value="DAYS">{t('common.days')}</Option>
                      </Select>
                    </Form.Item>
                  </Space>

                  <Form.Item
                    name="capacity"
                    label={t('partner.services.form.capacity')}
                    rules={[
                      { required: true, message: t('partner.services.form.capacityRequired') },
                      { type: 'number', min: 1, message: t('partner.services.form.capacityMin') }
                    ]}
                    tooltip={t('partner.services.form.capacityTooltip')}
                  >
                    <InputNumber
                      min={1}
                      style={{ width: '100%' }}
                      placeholder="1"
                    />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            name="price"
            label={t('partner.services.form.price')}
            rules={[
              { required: true, message: t('partner.services.form.priceRequired') },
              { type: 'number', min: 0.01, message: t('partner.services.form.priceMin') }
            ]}
          >
            <InputNumber
              min={0.01}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="50.00"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('common.active')}
            valuePropName="checked"
            tooltip={t('partner.services.form.activeTooltip')}
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={submitting}
                size="large"
              >
                {isEdit ? t('partner.services.updateService') : t('partner.services.createService')}
              </Button>
              <Button onClick={() => navigate('/partner/services')} size="large">
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
});

export default ServiceAddEdit;
